import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TOUR_REGISTRY, TourDefinition, TourStep } from './tour-registry';

@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly router = inject(Router);

  private readonly _activeTour = signal<TourDefinition | null>(null);
  private readonly _stepIndex = signal(0);
  private readonly _sandbox = signal(false);

  readonly activeTour = this._activeTour.asReadonly();
  readonly stepIndex = this._stepIndex.asReadonly();
  /** Czy trwa symulacja działania (bez zapisu). */
  readonly sandboxActive = this._sandbox.asReadonly();

  readonly currentStep = computed<TourStep | null>(() => {
    const tour = this._activeTour();
    return tour ? tour.steps[this._stepIndex()] ?? null : null;
  });
  readonly totalSteps = computed(() => this._activeTour()?.steps.length ?? 0);
  readonly isFirst = computed(() => this._stepIndex() === 0);
  readonly isLast = computed(() => this._stepIndex() >= this.totalSteps() - 1);

  /** Definicja samouczka przypisana do danej ścieżki trasy (bez query). */
  tourForRoute(path: string): TourDefinition | undefined {
    return TOUR_REGISTRY.find((t) => t.route === path);
  }

  /** Uruchamia samouczek po identyfikatorze; sandbox=true => symulacja bez zapisu. */
  start(id: string, sandbox = false): void {
    const tour = TOUR_REGISTRY.find((t) => t.id === id);
    if (!tour) {
      return;
    }
    this._sandbox.set(sandbox);
    const extras = sandbox ? { queryParams: { samouczek: 'sim' } } : {};
    void this.router.navigate([tour.route], extras).then(() => {
      this._activeTour.set(tour);
      this._stepIndex.set(0);
    });
  }

  /** Uruchamia samouczek przypisany do bieżącej trasy (przycisk w topbarze). */
  startForRoute(path: string): void {
    const tour = this.tourForRoute(path);
    if (tour) {
      this._sandbox.set(false);
      this._activeTour.set(tour);
      this._stepIndex.set(0);
    }
  }

  next(): void {
    if (!this._activeTour()) {
      return;
    }
    if (this.isLast()) {
      this.stop();
    } else {
      this._stepIndex.update((i) => i + 1);
    }
  }

  prev(): void {
    if (this._stepIndex() > 0) {
      this._stepIndex.update((i) => i - 1);
    }
  }

  stop(): void {
    this._activeTour.set(null);
    this._stepIndex.set(0);
    this._sandbox.set(false);
  }
}
